import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { purchaseService } from '../services/purchase.service';

// Thunks principaux
export const fetchPurchases = createAsyncThunk(
  'purchase/fetchPurchases',
  async (_, { rejectWithValue }) => {
    try {
      const response = await purchaseService.getAllPurchases();
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Erreur lors de la récupération des achats');
    }
  }
);

export const fetchPurchaseById = createAsyncThunk(
  'purchase/fetchPurchaseById',
  async (id, { rejectWithValue }) => {
    try {
      return await purchaseService.getPurchaseById(id);
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Erreur lors de la récupération de l\'achat');
    }
  }
);

export const createPurchase = createAsyncThunk(
  'purchase/createPurchase',
  async (purchase, { rejectWithValue }) => {
    try {
      const result = await purchaseService.createPurchase(purchase);
      return result;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Erreur lors de la création de l\'achat');
    }
  }
);

export const updatePurchase = createAsyncThunk(
  'purchase/updatePurchase',
  async ({ id, purchaseData }, { rejectWithValue }) => {
    try {
      const result = await purchaseService.updatePurchase(id, purchaseData);
      return result;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Erreur lors de la mise à jour de l\'achat');
    }
  }
);

export const softDeletePurchase = createAsyncThunk(
  'purchase/softDeletePurchase',
  async (id, { rejectWithValue }) => {
    try {
      await purchaseService.softDeletePurchase(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Erreur lors de la suppression de l\'achat');
    }
  }
);

export const hardDeletePurchase = createAsyncThunk(
  'purchase/hardDeletePurchase',
  async (id, { rejectWithValue }) => {
    try {
      await purchaseService.hardDeletePurchase(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Erreur lors de la suppression définitive de l\'achat');
    }
  }
);

export const fetchPurchasesByDateRange = createAsyncThunk(
  'purchase/fetchPurchasesByDateRange',
  async (filter, { rejectWithValue }) => {
    try {
      return await purchaseService.getPurchasesByDateRange(filter);
    } catch (error) {
      return rejectWithValue(
        error.response?.data || 'Erreur lors de la récupération des achats par date'
      );
    }
  }
);

// Initial state adapté au modèle Purchase
const initialState = {
  purchases: [],
  totalCount: 0,
  selectedPurchase: null,
  loading: false,
  error: null,
  showModal: false,
  showDeleteModal: false,
  searchQuery: '',
  searchResults: [],
  searchLoading: false,
  actionStatus: 'idle',
  filters: {
    statusId: null,
    supplierId: null,
    reference: '',
    invoiceNumber: '',
    purchaseDateRange: [null, null], // on peut stocker les ISO
  },
};

const purchaseSlice = createSlice({
  name: 'purchase',
  initialState,
  reducers: {
    resetActionStatus: (state) => {
      state.actionStatus = 'idle';
    },
    openModal: (state) => {
      state.showModal = true;
    },
    closeModal: (state) => {
      state.showModal = false;
    },
    openDeleteModal: (state) => {
      state.showDeleteModal = true;
    },
    closeDeleteModal: (state) => {
      state.showDeleteModal = false;
    },
    clearError: (state) => {
      state.error = null;
    },
    setSelectedPurchase: (state, action) => {
      state.selectedPurchase = action.payload;
    },
    setSearchQuery: (state, action) => {
      state.searchQuery = action.payload;
    },
    setPurchaseFilters: (state, action) => {
      state.filters = {
        ...state.filters,
        ...action.payload,
      };
    },
    clearPurchaseFilters: (state) => {
      state.filters = {
        statusId: null,
        supplierId: null,
        reference: '',
        invoiceNumber: '',
        purchaseDateRange: [null, null],
      };
    },
    clearSearchResults: (state) => {
      state.searchResults = [];
      state.searchQuery = '';
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchPurchases
      .addCase(fetchPurchases.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPurchases.fulfilled, (state, action) => {
        state.loading = false;
        state.actionStatus = 'idle';
        state.purchases = Array.isArray(action.payload) ? action.payload : (action.payload?.Items || []);
        state.totalCount = action.payload?.TotalCount || (Array.isArray(action.payload) ? action.payload.length : 0);
      })
      .addCase(fetchPurchases.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // fetchPurchaseById
      .addCase(fetchPurchaseById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPurchaseById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedPurchase = action.payload;
      })
      .addCase(fetchPurchaseById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // createPurchase
      .addCase(createPurchase.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createPurchase.fulfilled, (state, action) => {
        state.loading = false;
        state.purchases.push(action.payload);
        state.totalCount += 1;
        state.showModal = false;
      })
      .addCase(createPurchase.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // updatePurchase
      .addCase(updatePurchase.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updatePurchase.fulfilled, (state, action) => {
        state.loading = false;
        state.purchases = state.purchases.map((purchase) => {
          if (purchase.purchase_id === action.payload.purchaseId) return action.payload;
          return purchase;
        });
        state.showModal = false;
      })
      .addCase(updatePurchase.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // softDeletePurchase
      .addCase(softDeletePurchase.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(softDeletePurchase.fulfilled, (state, action) => {
        state.loading = false;
        state.purchases = state.purchases.filter((purchase) => purchase.purchaseId !== action.payload);
        state.totalCount -= 1;
        state.showDeleteModal = false;
      })
      .addCase(softDeletePurchase.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // hardDeletePurchase
      .addCase(hardDeletePurchase.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(hardDeletePurchase.fulfilled, (state, action) => {
        state.loading = false;
        state.purchases = state.purchases.filter((purchase) => purchase.purchaseId !== action.payload);
        state.totalCount -= 1;
        state.showDeleteModal = false;
      })
      .addCase(hardDeletePurchase.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchPurchasesByDateRange.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPurchasesByDateRange.fulfilled, (state, action) => {
        state.loading = false;
        state.actionStatus = 'idle';
        state.purchases = Array.isArray(action.payload)
          ? action.payload
          : action.payload?.items || [];
        state.totalCount = Array.isArray(action.payload)
          ? action.payload.length
          : action.payload?.totalCount || state.purchases.length;
      })
      .addCase(fetchPurchasesByDateRange.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const {
  resetActionStatus,
  openModal,
  closeModal,
  openDeleteModal,
  closeDeleteModal,
  clearError,
  setSelectedPurchase,
  setSearchQuery,
  clearSearchResults,
  setPurchaseFilters,
  clearPurchaseFilters,
} = purchaseSlice.actions;

export default purchaseSlice.reducer;
